import { useEffect, useRef } from 'react'

import { createSocketConnection } from '../../services/socketService'

import { getUserSession } from '../Utils'

/**
 * Assina um canal do socket e escuta um evento dele.
 *
 * A inscrição acontece DENTRO do efeito, e não no corpo do componente. Antes
 * ficava fora: `window.PusherEcho?.subscribe(channel)` rodava a cada render, e
 * cada chamada dispara a autorização do canal em /api/broadcasting/auth. Como o
 * `index.js` assina um canal de presença na raiz da aplicação, qualquer mudança
 * de estado global disparava uma nova autorização — centenas delas por minuto.
 *
 * Os callbacks ficam em ref porque são declarados no corpo de quem chama e
 * mudam de identidade a cada render: usá-los como dependência refaria a
 * inscrição o tempo todo, que é o problema que este arquivo existe para evitar.
 */
export const useSocket = ({ callback, channel, event, callbackUser }) => {
  const callbackRef = useRef(callback)
  const callbackUserRef = useRef(callbackUser)

  callbackRef.current = callback
  callbackUserRef.current = callbackUser

  useEffect(() => {
    const token = getUserSession()

    // Sem sessão não há canal privado possível: tentar só produz 401.
    if (!token || !channel) return undefined

    createSocketConnection(token)

    const pusher = window.PusherEcho

    if (!pusher) return undefined

    const canal = pusher.subscribe(channel)

    // Canal privado ou de presença sem autorização devolve 401, a inscrição
    // nunca se completa e cada nova tentativa bate de novo no endpoint. Desistir
    // aqui é o que impede a enxurrada quando a sessão expira.
    const aoRecusar = (erro) => {
      pusher.unsubscribe(channel)
      // eslint-disable-next-line no-console
      console.warn(`Socket: inscrição recusada em "${channel}"`, erro?.status ?? erro)
    }

    canal.bind('pusher:subscription_error', aoRecusar)
    canal.bind('pusher:member_added', (...args) => callbackUserRef.current?.(...args))
    canal.bind('pusher:member_removed', (...args) => callbackUserRef.current?.(...args))

    if (event) {
      canal.bind(event, (...args) => callbackRef.current?.(...args))
    }

    return () => {
      canal.unbind_all?.()
      pusher.unsubscribe(channel)
    }
  }, [channel, event])
}

export default useSocket
